import { useEffect, useRef, useState } from "react";
import { useTopLayerMenu } from "./useTopLayerMenu";

/** What the program's own `⋯` offers (2026-11-02, both boards). */
export type ProgramAction = "rename" | "archive";

/**
 * THE PROGRAM'S `⋯` IN THE BOARD'S HEAD — `Rename` and `Archive`. `WeekMenu`'s shape (a real `<button>`, focus into the menu
 * on open, arrows through the items, Escape back to the trigger, the top-layer list), its OWN class names on the SAME
 * declarations (`app.css`'s selector lists): a class name is a vocabulary, and this menu touches a program, not a week.
 *
 * ⚠️ A MENU, NOT A DIALOG: `Archive` hands back to the board, which owns the sentence about what an archive leaves alone.
 */
export function ProgramMenu({ name, onChoose }: { name: string; onChoose: (action: ProgramAction) => void }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const items: { kind: ProgramAction; label: string }[] = [
    { kind: "rename", label: "Rename" },
    { kind: "archive", label: "Archive" },
  ];

  useTopLayerMenu(open, trigger, list);

  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, [open]);

  function close(restoreFocus: boolean) {
    setOpen(false);
    setActive(0);
    if (restoreFocus) trigger.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { e.stopPropagation(); close(true); return; }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = e.key === "ArrowDown" ? (active + 1) % items.length : (active - 1 + items.length) % items.length;
      setActive(next);
      list.current?.querySelectorAll<HTMLButtonElement>("button")[next]?.focus();
    }
  }

  return (
    <div className="program-menu" onKeyDown={onKeyDown}>
      <button ref={trigger} type="button" className="program-menu-trigger"
              aria-haspopup="menu" aria-expanded={open} aria-label={`Actions for ${name}`}
              onClick={() => setOpen(!open)}>⋯</button>
      {open && (
        <ul ref={list} className="program-menu-list is-top-layer" role="menu" aria-label={`Actions for ${name}`}>
          {items.map((item) => (
            <li key={item.kind} role="none">
              <button type="button" role="menuitem"
                      className={item.kind === "archive" ? "program-menu-item is-destructive" : "program-menu-item"}
                      onClick={() => { close(false); onChoose(item.kind); }}>
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && <div className="program-menu-scrim" onClick={() => close(false)} aria-hidden="true" />}
    </div>
  );
}
